import { ObjectIdSchema } from '../../models/inventory';
import { z } from 'zod';
import { OfferIdSchema, OperationIdSchema } from '../../ids';
import { InventorySchema } from '../../models/inventory';
import {
  MerchantPricesSchema,
  OfferSchema,
  UnitPriceSchema,
  VaultViewSchema,
} from '../../models/market';
import { ItemIdSchema, PositiveQuantitySchema } from '../../models/items';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/**
 * The market as the player sees it (ECON-MARKET): every open offer of the auction, the player's own
 * recent trades (sold, bought, cancelled), what it keeps at each auction house and the town shop's
 * prices.
 */
export const getMarket = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/market`,
  access: 'session',
  response: z.object({
    offers: z.array(OfferSchema),
    history: z.array(OfferSchema),
    /** What the player keeps at each auction house, those holding something (MK-8). */
    vaults: z.array(VaultViewSchema),
    /** Rules the publish form needs (server config). */
    limits: z.object({
      maxOpenOffers: z.number().int().positive(),
      minUnitPrice: UnitPriceSchema,
      maxUnitPrice: UnitPriceSchema,
      /** A listing's deposit and a sale's commission, shares of its price (MK-7). */
      deposit: z.number().min(0).max(1),
      commission: z.number().min(0).max(1),
    }),
  }),
});

/** Every market command answers with the offer it touched (if any) and the player's holdings. */
const OfferResultSchema = z.object({
  operationId: OperationIdSchema,
  offer: OfferSchema,
  inventory: InventorySchema,
});

/**
 * Lists an offer at an auction house, standing by its door (MK-6): its goods leave the backpack for
 * the offer's escrow there, and its deposit (MK-7) is held from the treasury. Idempotent.
 */
export const publishOffer = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/market/offers`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    /** The auction house it is listed at (the layout's `auctionHouses`). */
    house: z.string().min(1).max(64),
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
    unitPrice: UnitPriceSchema,
    /** A unique object of the backpack (EQUIP-SLOT, EQ-4): its item, quantity 1. */
    object: ObjectIdSchema.optional(),
  }),
  response: OfferResultSchema,
});

/**
 * Buys a whole open offer from anywhere (MK-6): coins go to the seller, less the house's
 * commission, and the goods to the buyer's vault at the offer's house (MK-8), in one transaction.
 * Two simultaneous buyers: one sale, the other gets `offer_closed`. Idempotent.
 */
export const buyOffer = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/market/offers/:offerId/buy`,
  access: 'session',
  params: z.object({ offerId: OfferIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: OfferResultSchema,
});

/**
 * Cancels an own open offer from anywhere: its goods go to the seller's vault at its house and its
 * deposit is lost (MK-7). Idempotent.
 */
export const cancelOffer = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/market/offers/:offerId/cancel`,
  access: 'session',
  params: z.object({ offerId: OfferIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: OfferResultSchema,
});

const MerchantTradeSchema = z.object({
  operationId: OperationIdSchema,
  item: ItemIdSchema,
  quantity: PositiveQuantitySchema,
  /** The merchant's door traded at (C9, NP-1 a)), standing within `market.reach` of it. */
  merchant: z.string().min(1).max(64),
});

/**
 * Trades with an NPC merchant at its door, at its shop's prices: `buy` takes goods into the
 * backpack for coins, `sell` gives goods from the backpack for coins.
 */
export const tradeWithMerchant = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/market/merchant/:side`,
  access: 'session',
  params: z.object({ side: z.enum(['buy', 'sell']) }),
  body: MerchantTradeSchema,
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Takes goods or an object out of the own vault at an auction house into the backpack, standing by
 * its door (MK-8): what was bought there travels with whoever carries it. Idempotent.
 */
export const takeFromVault = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/market/houses/:house/take`,
  access: 'session',
  params: z.object({ house: z.string().min(1).max(64) }),
  body: z.object({
    operationId: OperationIdSchema,
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
    /** A unique object of the vault: its item, quantity 1. */
    object: ObjectIdSchema.optional(),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    inventory: InventorySchema,
    vaults: z.array(VaultViewSchema),
  }),
});

/**
 * A merchant's shop's prices now, a unit each way, and its stock (C9, NP-1 a), ECON-MARKET MK-2),
 * read from anywhere: trading asks to stand by its door.
 */
export const getMerchant = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/market/merchants/:merchantId`,
  access: 'session',
  params: z.object({ merchantId: z.string().min(1).max(64) }),
  response: MerchantPricesSchema,
});
